import { ELEMENTS } from "../constants";
import { camelCaseToHyphenCase } from "./string";

export const ELEMENT_MODIFIERS = {
  hover: "hover",
  focus: "focus",
  valid: "valid",
  invalid: "invalid",
};

export function handleCustomInputStyle({
  style,
  element,
  isSchemeChoiceEnabled,
}) {
  const styleElement = document.getElementById("custom-style");

  let styleContent = createInputStyleContent(style, element);

  if (isSchemeChoiceEnabled) {
    styleContent += createSelectStyleContent(style, ELEMENTS.SCHEME_CHOICE);
  }

  styleElement.innerHTML = styleContent;
}

export function handleCustomStyleForm(style, isSchemeChoiceEnabled) {
  const styleElement = document.getElementById("custom-style");

  let styleContent = createInputStyleContent(style, ELEMENTS.CARD_NUMBER);
  styleContent += createInputStyleContent(style, ELEMENTS.EXPIRY_DATE);
  styleContent += createInputStyleContent(style, ELEMENTS.CVV);

  if (isSchemeChoiceEnabled) {
    styleContent += createSelectStyleContent(style, ELEMENTS.SCHEME_CHOICE);
  }

  styleElement.innerHTML = styleContent;
}

export function handleCustomStyleAnimations(animation) {
  const styleAnimationElement = document.getElementById("custom-animation");
  styleAnimationElement.innerHTML = createCSSRules(animation, cssAnimationRule);
}

export function handleCustomSelectStyle(style, element) {
  const styleElement = document.getElementById("custom-style");
  styleElement.innerHTML = createSelectStyleContent(style, element);
}

export function createElementClass({ element, subElement, modifier }) {
  if (subElement && modifier) {
    return `${element}__${subElement}--${modifier}`;
  }

  if (modifier) {
    return `${element}--${modifier}`;
  }

  if (subElement) {
    return `${element}__${subElement}`;
  }
}

function cssAnimationRule(key, value) {
  return `@keyframes ${key} { ${value} } @-webkit-keyframes ${key} { ${value} }`;
}

function createInputStyleContent(style, element) {
  const { placeholder } = style;

  let styleContent = createCommonStyleContent({
    style,
    element,
    elementType: "input",
  });

  if (placeholder && placeholder.base)
    styleContent += createPlaceholderRule(placeholder.base, element);
  if (placeholder && placeholder.focus)
    styleContent += createPlaceholderRule(
      placeholder.focus,
      element,
      ELEMENT_MODIFIERS.focus
    );

  return styleContent;
}

function createSelectStyleContent(style) {
  const { schemeChoiceBorder } = style;

  let styleContent = "";

  if (schemeChoiceBorder && schemeChoiceBorder.base?.color !== undefined) {
    styleContent += createStylesheet(
      {
        color: schemeChoiceBorder.base.color,
        borderColor: schemeChoiceBorder.base.color,
      },
      /* When changing this CSS selector, make sure to update it in SchemeChoiceRadioButtonBase.css line 42 */
      `input[type="radio"]:checked + .scheme-choice--radio-label > .check-icon--container`
    );
  }

  if (style.base?.backgroundColor !== undefined) {
    styleContent += createStylesheet(
      { backgroundColor: style.base.backgroundColor },
      "#checkout-frames-radio-selector"
    );
  }
  return styleContent;
}

function createCommonStyleContent({ style, element, elementType, subElement }) {
  const { base, hover, focus, valid, invalid, autofill } = style;
  let styleContent = "";

  const createCSSHeader = (modifier) =>
    createStylesheetHeader({ element, elementType, subElement, modifier });

  if (base) {
    styleContent += createStylesheet(base, createCSSHeader());
  }

  if (autofill) {
    styleContent += createAutofillRule(autofill, element);
  }

  if (hover) {
    styleContent += createStylesheet(
      hover,
      createCSSHeader(ELEMENT_MODIFIERS.hover)
    );
  }

  if (focus) {
    styleContent += createStylesheet(
      focus,
      createCSSHeader(ELEMENT_MODIFIERS.focus)
    );
  }

  if (valid) {
    styleContent += createStylesheet(
      valid,
      createCSSHeader(ELEMENT_MODIFIERS.valid)
    );
  }

  if (invalid) {
    styleContent += createStylesheet(
      invalid,
      createCSSHeader(ELEMENT_MODIFIERS.invalid)
    );
  }

  return styleContent;
}

const cssRule = (key, value) => `${camelCaseToHyphenCase(key)}: ${value};`;

function createStylesheet(styleObj, header) {
  const rules = createCSSRules(styleObj, cssRule);

  return `${header} { ${rules} }`;
}

function createStylesheetHeader({
  element,
  modifier,
  subElement,
  elementType = "",
}) {
  const base = `${elementType}.${element}`;
  const additionalClass = createElementClass({ element, subElement, modifier });

  if (subElement) {
    return `${elementType}.${additionalClass}`;
  }

  if (additionalClass) {
    return `${base}.${additionalClass}`;
  }

  return `${base}`;
}

function createPlaceholderRule(styleObj, element, modifier) {
  let styleRule = "";

  styleRule += createCSSRules(styleObj, cssRule);
  const className =
    typeof modifier === "undefined"
      ? element
      : `${element}.${element}--${modifier}`;

  return `
  input.${className}::placeholder {${styleRule}}
  input.${className}::-webkit-input-placeholder {${styleRule}}
  input.${className}::-moz-placeholder {${styleRule}}
  input.${className}:-ms-input-placeholder {${styleRule}}
  `;
}

function createAutofillRule(styleObj, element) {
  const baseAutofillStyleRules = createCSSRules(styleObj, cssRule);

  return `
  .${element}:-webkit-autofill {${baseAutofillStyleRules}}
  .${element}.field--autofilled {${baseAutofillStyleRules}}
  `;
}

// createCSSRules :: object, function -> string
function createCSSRules(o, pattern) {
  let s = "";
  for (const key in o) {
    sanitizeStyle(key);
    const value = o[key];
    sanitizeStyle(value);
    s += pattern(key, value);
    s += getSpecialCaseRules(key, value, pattern);
  }
  return s;
}

function getSpecialCaseRules(key, value, pattern) {
  let s = "";

  if (key === "color") {
    s += pattern("-webkit-text-fill-color", value);
  }

  if (key === "backgroundColor") {
    s += pattern("-webkit-box-shadow", `inset 0 0 0 30px ${value}`);
  }

  return s;
}

// Invalid/Suspicious characters in css (xss css injection)
const evilRegexes = [
  // eslint-disable-next-line
  /(\bdata:\b|eval|cookie|\bwindow\b|\bparent\b|\bthis\b)/i, // suspicious javascript-type words
  // eslint-disable-next-line
  /behaviou?r|expression|moz-binding|@import|@charset|(java|vb)?script|[\<]|\\\w/i,
  // eslint-disable-next-line
  /[\<>]/, // back slash, html tags,
  // eslint-disable-next-line
  /(?![éºãúüóçäáö])[\x7f-\xff]/, // high bytes -- suspect
  // eslint-disable-next-line
  /[\x00-\x08\x0B\x0C\x0E-\x1F]/, //low bytes -- suspect
  // eslint-disable-next-line
  /&\#/, // bad charset
];
// sanitizer inspired by https://github.com/courtenay/css_file_sanitize/blob/master/lib/css_sanitize.rb
function sanitizeStyle(style) {
  for (let evilRegex of evilRegexes) {
    if (evilRegex.test(style)) throw new Error("Invalid css");
  }
}
